import React, { useState } from 'react'
import styled from 'styled-components'

import { formatNumber } from '../../utils'

const Range = styled.div`
    display: flex;

    input:first-child {
        margin-right: 8px;
    }
`

type BaseProps = {
    unitSet: UnitSet
    curves: Curves
    disabled: boolean
}

type InputProps = BaseProps & {
    threshold: number | ''
    onChange: (value: number | '') => void
    channel: string
    min?: number
}

type ThresholdInputProps = BaseProps & {
    value: Threshold
    onChange: (event: { target: { value: Threshold } }) => void
    state: { channel: string; comparator: ComparatorOp | ''; threshold: Threshold }
}

const Input = ({ threshold, channel, onChange, unitSet, curves, disabled, min }: InputProps): React.ReactElement => {
    const unit = unitSet?.data?.[channel]?.unit || curves?.[channel]?.unit || ''

    const [value, setValue] = useState<number | string>(
        unit
            ? threshold || threshold === 0
                ? formatNumber(threshold, unitSet?.data?.[channel], curves?.[channel], true) + ' ' + unit
                : ''
            : threshold
    )
    const [focused, toggleFocused] = useState(false)

    return (
        <input
            type={focused ? 'number' : 'text'}
            value={value}
            disabled={disabled}
            style={{ color: '#606a80' }}
            min={isNaN(min) || (!min && min !== 0) ? undefined : min}
            step="any"
            onFocus={() => {
                toggleFocused(true)
                setValue(threshold)
            }}
            onBlur={() => {
                toggleFocused(false)
                setValue(v =>
                    v || v === 0 ? formatNumber(v, unitSet?.data?.[channel], curves?.[channel], true) + ' ' + unit : ''
                )
            }}
            onChange={e => {
                setValue(e.target.value)

                const newValue = parseFloat(e.target.value)
                onChange(isNaN(newValue) ? '' : newValue)
            }}
        />
    )
}

const ThresholdInput = ({ value, state, onChange, ...props }: ThresholdInputProps): React.ReactElement => {
    if (state.comparator === 'in range' || state.comparator === 'outside range') {
        const values: Threshold = value === '' ? ['', ''] : Array.isArray(value) ? value : [value, value + 1]

        return (
            <Range>
                <Input
                    threshold={values[0]}
                    channel={state.channel}
                    onChange={newValue => onChange({ target: { value: [newValue, values[1]] } })}
                    {...props}
                />
                <Input
                    threshold={values[1]}
                    channel={state.channel}
                    onChange={newValue => onChange({ target: { value: [values[0], newValue] } })}
                    min={typeof values[0] === 'number' && values[0]}
                    {...props}
                />
            </Range>
        )
    }

    return (
        <Input
            threshold={Array.isArray(value) ? value[0] : value}
            channel={state.channel}
            onChange={newValue => onChange({ target: { value: newValue } })}
            {...props}
        />
    )
}

export default ThresholdInput
